import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import {
  hasDeferredPrompt,
  isIosSafari,
  markInstallPromptDismissed,
  triggerNativeInstall,
} from '../utils/pwaInstall';

interface Props {
  open: boolean;
  onClose: () => void;
}

// Shown on first access from the public MainLayout (and contextually in the
// mobile field flow). Android Chrome gets a native install button
// (Workbox-captured beforeinstallprompt event); iOS Safari gets a coach mark
// explaining the Share → Add-to-Home-Screen flow.
export default function InstallPrompt({ open, onClose }: Props) {
  const [installing, setInstalling] = useState(false);
  const canInstallNatively = hasDeferredPrompt();
  const iosCoachMark = !canInstallNatively && isIosSafari();

  const handleDismiss = (): void => {
    markInstallPromptDismissed();
    onClose();
  };

  const handleInstall = async (): Promise<void> => {
    setInstalling(true);
    const outcome = await triggerNativeInstall();
    setInstalling(false);
    if (outcome !== 'accepted') markInstallPromptDismissed();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleDismiss} maxWidth="xs" fullWidth>
      <DialogTitle>Install WeSafe on your phone</DialogTitle>
      <DialogContent>
        {canInstallNatively && (
          <DialogContentText>
            Add WeSafe to your home screen for one-tap access on site. Works
            offline so you can still sign on with bad reception.
          </DialogContentText>
        )}
        {iosCoachMark && (
          <Stack spacing={1.5}>
            <DialogContentText>
              On iPhone, add WeSafe to your home screen in two taps:
            </DialogContentText>
            <Box component="ol" sx={{ pl: 3, m: 0, '& li': { mb: 1 } }}>
              <li>
                Tap the <strong>Share</strong> icon at the bottom of Safari (a
                square with an up-arrow).
              </li>
              <li>
                Scroll down and tap <strong>Add to Home Screen</strong>.
              </li>
            </Box>
          </Stack>
        )}
        {!canInstallNatively && !iosCoachMark && (
          <DialogContentText>
            WeSafe is already installed — or your browser doesn&apos;t support
            installing this app.
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDismiss} disabled={installing}>
          Not now
        </Button>
        {canInstallNatively && (
          <Button variant="contained" onClick={handleInstall} disabled={installing}>
            {installing ? 'Installing…' : 'Install'}
          </Button>
        )}
        {iosCoachMark && (
          <Button variant="contained" onClick={handleDismiss}>
            Got it
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
